import * as Immutable from 'immutable';
import ProductOptionStore from '../../../../AppData/_stores/ProductOptionStore';
import ProductOptionTypeStore from '../../../../AppData/_stores/ProductOptionTypeStore';

type StoreVariantLimitConfig = {
	max: number
	ignoredOptionTypeIds?: number[]
	requiresCompleteOptionMatrix?: boolean
}

export type StoreVariantLimitResult = {
	count: number
	max: number
	exceeded: boolean
	requiresCompleteOptionMatrix: boolean
}

export type StoreVariantOptionTypes = Immutable.Map<string, ProductOptionTypeStore> | Immutable.OrderedMap<string, ProductOptionTypeStore>

const STORE_VARIANT_LIMIT_CONFIGS: Record<string, StoreVariantLimitConfig> = {
	etsy: {
		max: 70,
		ignoredOptionTypeIds: [4, 5, 31, 32, 33, 37],
		requiresCompleteOptionMatrix: true,
	},
	shopify: {
		max: 2048,
		ignoredOptionTypeIds: [4, 5, 31, 32, 33, 37],
	},
	squarespace: {
		max: 250,
		ignoredOptionTypeIds: [4, 5, 31, 32, 33, 37],
	},
};

export class StoreVariantLimit {
	/**
	 * Returns whether an option type is excluded from a store's variant-limit calculation.
	 */
	static isIgnoredOptionType(store: string, optionTypeId: number): boolean {
		return STORE_VARIANT_LIMIT_CONFIGS[store]?.ignoredOptionTypeIds?.includes(optionTypeId) === true;
	}

	/**
	 * Calculates the store-specific variant limit state for the currently selected options.
	 * Returns null for stores that do not have a configured frontend variant limit.
	 */
	static calculate(
		store: string,
		availableOptionTypes: StoreVariantOptionTypes | undefined,
		disabledOptionIds: Immutable.List<number> = Immutable.List<number>(),
	): StoreVariantLimitResult | null {
		const config = STORE_VARIANT_LIMIT_CONFIGS[store];
		if (!config) {
			return null;
		}

		const disabledOptionIdSet = new Set(disabledOptionIds.toArray());
		const optionGroups: ProductOptionStore[][] = [];
		let hasLimitedOptionType = false;
		let hasEmptyOptionType = false;

		availableOptionTypes?.forEach(type => {
			if (config.ignoredOptionTypeIds?.includes(type.get('id'))) {
				return;
			}

			hasLimitedOptionType = true;
			const options: ProductOptionStore[] = [];
			type.get('options').forEach(option => {
				if (!disabledOptionIdSet.has(option.get('id'))) {
					options.push(option);
				}
			});

			if (options.length === 0) {
				hasEmptyOptionType = true;
				return;
			}

			optionGroups.push(options);
		});

		const count = hasEmptyOptionType ? 0 : (hasLimitedOptionType ? StoreVariantLimit.countCombinations(config, optionGroups) : 1);

		return {
			count,
			max: config.max,
			exceeded: count > config.max,
			requiresCompleteOptionMatrix: config.requiresCompleteOptionMatrix === true,
		};
	}

	/**
	 * Checks whether a selected option set satisfies option dependency rules.
	 */
	private static respectsDependencies(options: ProductOptionStore[]): boolean {
		const optionIds = new Set(options.map(option => option.get('id')));

		return options.every(option => {
			const dependencies = option.get('listDependencies');
			if (dependencies.count() === 0) {
				return true;
			}

			return dependencies.some(dependencyId => optionIds.has(dependencyId));
		});
	}

	/**
	 * Counts the store rows required for the selected option groups.
	 */
	private static countCombinations(config: StoreVariantLimitConfig, optionGroups: ProductOptionStore[][]): number {
		if (config.requiresCompleteOptionMatrix) {
			return StoreVariantLimit.countCompleteMatrixCombinations(config, optionGroups);
		}

		return StoreVariantLimit.countDependencyValidCombinations(config, optionGroups);
	}

	private static countDependencyValidCombinations(config: StoreVariantLimitConfig, optionGroups: ProductOptionStore[][], index = 0, selectedOptions: ProductOptionStore[] = []): number {
		if (index >= optionGroups.length) {
			return StoreVariantLimit.respectsDependencies(selectedOptions) ? 1 : 0;
		}

		let count = 0;
		for (const option of optionGroups[index]) {
			count += StoreVariantLimit.countDependencyValidCombinations(config, optionGroups, index + 1, [...selectedOptions, option]);
			if (count > config.max) {
				return count;
			}
		}

		return count;
	}

	/**
	 * Counts rows after Etsy's final matrix pass: collect values from real,
	 * dependency-valid variants, then cross-product those values.
	 */
	private static countCompleteMatrixCombinations(config: StoreVariantLimitConfig, optionGroups: ProductOptionStore[][]): number {
		const valuesByGroup = optionGroups.map(() => new Set<string>());

		StoreVariantLimit.collectCompleteMatrixValues(config, optionGroups, valuesByGroup);

		return StoreVariantLimit.countMatrixValues(valuesByGroup);
	}

	private static collectCompleteMatrixValues(config: StoreVariantLimitConfig, optionGroups: ProductOptionStore[][], valuesByGroup: Set<string>[], index = 0, selectedOptions: ProductOptionStore[] = []): boolean {
		if (index >= optionGroups.length) {
			// Only real variants contribute values to the final Etsy matrix.
			if (!StoreVariantLimit.respectsDependencies(selectedOptions)) {
				return false;
			}

			selectedOptions.forEach((option, optionIndex) => {
				valuesByGroup[optionIndex].add(option.get('slug'));
			});

			// Stop walking once we already know Etsy's row limit is exceeded.
			return StoreVariantLimit.countMatrixValues(valuesByGroup) > config.max;
		}

		for (const option of optionGroups[index]) {
			if (StoreVariantLimit.collectCompleteMatrixValues(config, optionGroups, valuesByGroup, index + 1, [...selectedOptions, option])) {
				return true;
			}
		}

		return false;
	}

	private static countMatrixValues(valuesByGroup: Set<string>[]): number {
		let count = 1;

		for (const values of valuesByGroup) {
			// A complete matrix does not exist until each option group has a value.
			if (values.size === 0) {
				return 0;
			}

			count *= values.size;
		}

		return count;
	}
}
